import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useSelector } from 'react-redux';
import { selectDefaultFacility, selectPrimaryFacility } from '../../redux/slices/facilitiesSlice';
import { getClockEvents } from '../../services/clockEventService';
import ClockInOutModal from './ClockInOutModal';
import { 
  Snackbar, 
  Alert, 
  Box, 
  Typography, 
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  ExitToApp as ClockOutIcon,
  Login as ClockInIcon
} from '@mui/icons-material';

// Inner component that only renders when we have a valid primary facility
const ClockInOutManagerInner = ({ user, primaryFacility }) => {
  const [showClockModal, setShowClockModal] = useState(false);
  const [clockAction, setClockAction] = useState(null);
  const [lastClockEvent, setLastClockEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Now we can safely call useFeatureFlag because we know primaryFacility exists
  const { isEnabled: schedulingEnabled, loading: featureLoading } = useFeatureFlag(
    'sch', 
    primaryFacility.id
  );

  const fetchLastClockEvent = useCallback(async () => {
    try {
      const filters = {
        employeeId: user.uid,
        facilityId: primaryFacility.id
      };
      
      const result = await getClockEvents(primaryFacility.id, filters, 1, 1);
      if (result.clockEvents && result.clockEvents.length > 0) {
        setLastClockEvent(result.clockEvents[0]);
      }
    } catch (error) {
      console.error('Error fetching last clock event:', error);
    }
  }, [user.uid, primaryFacility.id]);

  useEffect(() => {
    if (user && primaryFacility && schedulingEnabled && !featureLoading) {
      fetchLastClockEvent();
    }
  }, [user, primaryFacility, schedulingEnabled, featureLoading, fetchLastClockEvent]);

  const handleClockIn = () => {
    setClockAction('clock_in');
    setShowClockModal(true);
  };

  const handleClockOut = () => {
    setClockAction('clock_out');
    setShowClockModal(true);
  };

  const handleClockSuccess = async (action) => {
    const message = action === 'clock_in' 
      ? 'Successfully clocked in!' 
      : 'Successfully clocked out! You will be signed out shortly.';
    setSnackbar({ open: true, message, severity: 'success' });
    
    // Auto close the modal
    setShowClockModal(false);
    
    // Immediately dispatch event with status information for instant UI updates
    const newClockStatus = action === 'clock_in';
    console.log('🚀 Dispatching immediate clock status update:', newClockStatus);
    
    window.dispatchEvent(new CustomEvent('clockStatusUpdate', {
      detail: { 
        isClockedIn: newClockStatus,
        action: action,
        timestamp: new Date().toISOString()
      }
    }));
    
    // Add a small delay to ensure database write has propagated, then refresh and notify
    setTimeout(async () => {
      // Refresh last clock event
      await fetchLastClockEvent();
      
      // Notify other components of status change (legacy event)
      window.dispatchEvent(new CustomEvent('clockInStatusChanged'));
      
      console.log('📢 Clock status change events dispatched');
    }, 500); // 500ms delay to allow database propagation
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isCurrentlyClockedIn = () => {
    if (!lastClockEvent) return false;
    return lastClockEvent.action === 'clock_in';
  };

  const getLastEventTime = () => {
    if (!lastClockEvent) return null;
    return new Date(lastClockEvent.actualTime).toLocaleString();
  };

  const getStatusColor = () => {
    return isCurrentlyClockedIn() ? 'success' : 'default';
  };

  const getStatusText = () => {
    if (!lastClockEvent) return 'No clock events today';
    const action = lastClockEvent.action === 'clock_in' ? 'Clocked In' : 'Clocked Out';
    const time = getLastEventTime();
    return `${action} at ${time}`;
  };

  // Don't render if scheduling feature is not enabled or still loading
  if (featureLoading) {
    return null; // Could show a loading spinner if desired
  }

  if (!schedulingEnabled) {
    return null;
  }

  return (
    <>
      <Card sx={{ mb: 2, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="600">
                  Time Clock - {primaryFacility.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getStatusText()}
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={isCurrentlyClockedIn() ? 'Clocked In' : 'Clocked Out'}
                color={getStatusColor()}
                size="small"
                variant={isCurrentlyClockedIn() ? 'filled' : 'outlined'}
              />
              
              {isCurrentlyClockedIn() ? (
                <Tooltip title="Clock Out">
                  <IconButton 
                    onClick={handleClockOut}
                    color="error"
                    size="small"
                  >
                    <ClockOutIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Clock In">
                  <IconButton 
                    onClick={handleClockIn}
                    color="primary"
                    size="small"
                  >
                    <ClockInIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <ClockInOutModal
        open={showClockModal}
        onClose={() => setShowClockModal(false)}
        user={user}
        facilityId={primaryFacility.id}
        action={clockAction}
        onSuccess={handleClockSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const ClockInOutManager = () => {
  const { user } = useAuth();
  const defaultFacility = useSelector(selectDefaultFacility);
  const primaryFacility = useSelector(selectPrimaryFacility);

  // Only render the inner component when we have both user and primaryFacility
  // This prevents useFeatureFlag from being called with null during initialization
  if (!user || !primaryFacility) {
    console.log('ClockInOutManager waiting for user and facility context...', {
      hasUser: !!user,
      hasDefaultFacility: !!defaultFacility,
      hasPrimaryFacility: !!primaryFacility
    });
    return null;
  }

  return <ClockInOutManagerInner user={user} primaryFacility={primaryFacility} />;
};

export default ClockInOutManager; 